<script setup lang="ts">
import { computed, ref } from 'vue';
import { Plus } from 'lucide-vue-next';
import { Button } from '@/components/ui/button';
import { usePermissions } from '@/composables/usePermissions';
import { zoneSummaryFor } from '@/composables/api/useZoneSummaries';
import { withPendingScheduleWrites } from '@/composables/api/scheduleItems';
import ScheduleGroupedList from './ScheduleGroupedList.vue';
import ScheduleEditorDialog from './ScheduleEditorDialog.vue';
import ScheduleRowActions from './ScheduleRowActions.vue';
import { positiveZoneId } from './scheduleGroups';

/**
 * The grouped schedule list with its write actions (amendment A2): "Add
 * schedule" in the header creates a schedule on the list's own zone; every row
 * has Edit and Delete, subject to the location_zone_custom_playlist_schedule
 * model permission (the server's row-level check still decides, and a 403
 * shows a plain message); rows that belong to another zone also get "Open
 * zone". Station change rows open read-only ("View").
 *
 * The rows come from the list document the caller holds, which can be older
 * than this browser's last save: the table drawer and the overlay inspector
 * hold a snapshot of the row they were opened from, and on staging and
 * production the reindex is queued. So the items are read through the
 * module-wide pending writes (scheduleItems.ts), which survive this component
 * being unmounted by a tab switch or an inspector push and pop.
 */
const props = withDefaults(
    defineProps<{
        items: unknown;
        listId: unknown;
        ownerZoneId: unknown;
        ownerZoneName?: string | null;
        listName?: string | null;
        density?: 'line' | 'card';
        scrollBody?: boolean;
        /** A schedule to point at: shown, scrolled to and highlighted. */
        highlightScheduleId?: string | null;
    }>(),
    { ownerZoneName: null, listName: null, density: 'line', scrollBody: false, highlightScheduleId: null },
);

const { can } = usePermissions();

const PERMISSION_KEY = 'location_zone_custom_playlist_schedule';
const canCreate = computed(() => can('create', PERMISSION_KEY));

const listIdNumber = computed(() => positiveZoneId(props.listId));
const ownerId = computed(() => positiveZoneId(props.ownerZoneId));
/** Writing needs both ids; without them the list renders read-only. */
const writable = computed(() => listIdNumber.value !== null && ownerId.value !== null);

const items = computed<unknown>(() => withPendingScheduleWrites(props.listId, props.items));

const editorOpen = ref(false);

function openCreate(): void {
    editorOpen.value = true;
}

const ownerZoneNameResolved = computed(
    () => props.ownerZoneName ?? (ownerId.value !== null ? (zoneSummaryFor(ownerId.value)?.displayString ?? null) : null),
);

</script>

<template>
    <!-- Keyed by list: the drawer reuses this instance when another row's badge
         is clicked, and the tab, filter text and collapsed groups belong to one list. -->
    <ScheduleGroupedList
        :key="String(listId)"
        :items="items"
        :owner-zone-id="ownerZoneId"
        :owner-zone-name="ownerZoneName"
        :density="density"
        :scroll-body="scrollBody"
        :highlight-schedule-id="highlightScheduleId"
    >
        <template v-if="writable && canCreate" #toolbar>
            <Button size="sm" variant="outline" class="h-7 shrink-0" data-testid="schedule-add" @click="openCreate">
                <Plus class="size-3.5" aria-hidden="true" />
                Add schedule
            </Button>
        </template>

        <template #row-actions="{ schedule, group }">
            <ScheduleRowActions
                :schedule="schedule"
                :list-id="listId"
                :list-name="listName"
                :owner-zone-id="ownerZoneId"
                :owner-zone-name="ownerZoneNameResolved"
                :zone-id="group.zoneId"
                :zone-name="group.zoneName"
            />
        </template>
    </ScheduleGroupedList>

    <ScheduleEditorDialog
        v-if="writable"
        v-model:open="editorOpen"
        :schedule="null"
        :list-id="listIdNumber!"
        :list-name="listName"
        :owner-zone-id="ownerId!"
        :owner-zone-name="ownerZoneNameResolved"
        :read-only="!canCreate"
    />
</template>
